import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: '#f8f9fb', borderRadius: 10, padding: '1rem', flex: 1 }}>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 4px' }}>{label}</p>
      <p style={{ fontSize: 24, fontWeight: 600, margin: 0, color: color || '#111827' }}>{value}</p>
    </div>
  );
}

export default function BulkCampaignReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedBusiness } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/campaigns/bulk-report/${id}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e.response?.data?.error || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ padding: '2rem' }}>Loading report...</div>;
  if (error) return <div style={{ padding: '2rem', color: '#dc2626' }}>{error}</div>;
  if (!data) return null;

  const { campaign, failure_breakdown, tag_name, failed_contacts } = data;
  const sent = campaign.sent_count || 0;
  const delivered = campaign.delivered_count || 0;
  const read = campaign.read_count || 0;
  const failed = campaign.failed_count || 0;
  const deliveryRate = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : '0.0';
  const maxFailCount = failure_breakdown.length ? Math.max(...failure_breakdown.map(f => f.cnt)) : 1;

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: 900 }}>
      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12 }}>
        ← Back
      </button>

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{campaign.campaign_name}</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
          Sent {fmtDate(campaign.schedule_time)} · {campaign.template_name}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <StatCard label="Sent" value={sent} />
        <StatCard label="Delivered" value={delivered} color="#16a34a" />
        <StatCard label="Read" value={read} />
        <StatCard label="Failed" value={failed} color="#dc2626" />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Delivery rate</p>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>{deliveryRate}%</p>
        </div>
        <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${deliveryRate}%`, background: '#16a34a' }} />
        </div>
      </div>

      {failure_breakdown.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: 16 }}>
          <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px' }}>Why messages failed</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {failure_breakdown.map((f, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span>{f.error_code ? `[${f.error_code}] ` : ''}{f.error_reason || 'Unknown reason'}</span>
                  <span style={{ fontWeight: 600 }}>{f.cnt}</span>
                </div>
                <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(f.cnt / maxFailCount) * 100}%`, background: '#f59e0b' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem' }}>
        <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 10px' }}>Audience</p>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <tbody>
            {campaign.audience_type && (
              <tr>
                <td style={{ color: '#6b7280', padding: '4px 0' }}>Audience type</td>
                <td style={{ textAlign: 'right', padding: '4px 0' }}>{campaign.audience_type}</td>
              </tr>
            )}
            {tag_name && (
              <tr>
                <td style={{ color: '#6b7280', padding: '4px 0' }}>Selected by tag</td>
                <td style={{ textAlign: 'right', padding: '4px 0' }}>{tag_name}</td>
              </tr>
            )}
            <tr>
              <td style={{ color: '#6b7280', padding: '4px 0' }}>Total contacts</td>
              <td style={{ textAlign: 'right', padding: '4px 0' }}>{campaign.total_contacts}</td>
            </tr>
            <tr>
              <td style={{ color: '#6b7280', padding: '4px 0', fontWeight: 600 }}>Sent to</td>
              <td style={{ textAlign: 'right', padding: '4px 0', fontWeight: 600 }}>{sent}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {failed_contacts && failed_contacts.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 12px' }}>
            <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Failed Contacts ({failed_contacts.length})</p>
            <button
              onClick={() => {
                const header = ['Name', 'Phone', 'Error Code', 'Reason'];
                const q = '"';
                const rows = failed_contacts.map(f => [
                  q + (f.name || '-').replace(/"/g, '""') + q,
                  q + '=""' + f.phone + '""' + q,
                  q + (f.error_code || '') + q,
                  q + (f.error_reason || 'Unknown').replace(/"/g, '""') + q
                ]);
                const csvContent = [header.map(h => q + h + q).join(','), ...rows.map(r => r.join(','))]
                  .join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `failed_contacts_${id || 'report'}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
              style={{ fontSize: 12, fontWeight: 500, padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}
            >
              ⬇ Download CSV
            </button>
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Name</th>
                  <th style={{ padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Phone</th>
                  <th style={{ padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {failed_contacts.map((f, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '6px 8px' }}>{f.name || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{f.phone}</td>
                    <td style={{ padding: '6px 8px', color: '#dc2626' }}>{f.error_code ? `[${f.error_code}] ` : ''}{f.error_reason || 'Unknown'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
